import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import { Box, Checkbox, Collapse, FormControlLabel, Popover, Switch, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collapsedButtons,
  CREATE_PAGE_INFO,
  MYBINNYS_APP_PAGE_INFO,
  SCAN_APP_PAGE_INFO,
  NEXUS_WEB_PAGE_INFO,
  FAB_PAGE_INFO,
  BRECO_PAGE_INFO,
  AIRA_APP_PAGE_INFO,
  EXPO_PAGE_INFO,
  TOGGLE_TAB_CONFIG,
  DEFAULT_RIGHTS_MAP,
  sectionObject,
  SELECT_PAGE_INFO,
  TAB_TO_MENU_MAP,
  tabNames,
  TABS,
  TRACK_APP_PAGE_INFO,
  useRightsValidation,
  VISION_PAGE_INFO,
  CRECO_PAGE_INFO,
  TECHIE_PAGE_INFO,
  ClientMaster_PAGE_INFO,
  PURCHIE_PAGE_INFO,
  CRM_PAGE_INFO,
} from "../../../constants/rightsConstant.js";
// Styles
import "./NewRightsPage.css";
// Context & APIs
import { KeyboardDoubleArrowRight } from "@mui/icons-material";
import RemoveModal from "components/ui/RemoveModal/RemoveModal";
import { usePermissions } from "context/PermissionsContext";
import { useSettingContext } from "context/SettingContext";
import { toast } from "react-toastify";
import { findSpecificMenu, findSpecificSubMenu, isAllowed } from "utils/checkUserRigths";
import { saveRights } from "../Rights/api/save-rights";
import { getAllRoles, getBrecoClientGroups, getSpecificRole } from "../Role/Apis/get-role";
import RightsTable from "./rightsTable.jsx";

/** Select → Quotes: only one of All | Own | None (legacy/API may send multiple allowed). */
const normalizeSelectQuotesPermissions = (labels) => {
  if (!labels?.length) return [];
  if (labels.includes("All")) return ["All"];
  if (labels.includes("Own")) return ["Own"];
  if (labels.includes("None")) return ["None"];
  return [];
};

/** Items where only one option can be selected at a time -> default option when switched on. */
const SINGLE_SELECT_ITEMS = {
  "Data Access_Data Access": "All Data",
};

// Menu-scoped (source) map: rights `menu` field -> page info source, so same-named
// subMenus across sources (e.g. Vision's "Settings > Quotes" vs Select's "Quotes")
// don't clash when validating permission labels.
const MENU_TO_PAGE_INFO = {
  "Vision Web": VISION_PAGE_INFO,
  Create: CREATE_PAGE_INFO,
  "Track App": TRACK_APP_PAGE_INFO,
  "My Binny's": MYBINNYS_APP_PAGE_INFO,
  Select: SELECT_PAGE_INFO,
  Scan: SCAN_APP_PAGE_INFO,
  Nexus: NEXUS_WEB_PAGE_INFO,
  Fab: FAB_PAGE_INFO,
  Aira: AIRA_APP_PAGE_INFO,
  Breco: BRECO_PAGE_INFO,
  Creco: CRECO_PAGE_INFO,
  Techie: TECHIE_PAGE_INFO,
  CRM: CRM_PAGE_INFO,
};

// CRM: rows keyed by subMenu name; parent-child lock map from CRM_PAGE_INFO
const CRM_SUB_ITEMS = CRM_PAGE_INFO.flatMap((section) => section.subMenus.map((sub) => ({ ...sub, section: section.name })));
const CRM_ROW_NAMES = CRM_SUB_ITEMS.map((sub) => sub.name);
const CRM_PARENTS = CRM_SUB_ITEMS.reduce((acc, sub) => {
  if (sub.parent) acc[sub.name] = Array.isArray(sub.parent) ? sub.parent : [sub.parent];
  return acc;
}, {});
const isCrmParentOn = (rowName, checked) => {
  const parents = CRM_PARENTS[rowName];
  return !parents || parents.some((parent) => !!checked[parent]);
};

const buildValidLabelsMap = () => {
  const map = {};
  Object.entries(MENU_TO_PAGE_INFO).forEach(([menuName, items]) => {
    if (!Array.isArray(items)) return;
    const subMap = {};
    items.forEach((item) => {
      if (item.subMenus) {
        item.subMenus.forEach((sub) => {
          if (sub.name && sub.options) subMap[sub.name] = sub.options;
        });
      } else if (item.name && item.options) {
        subMap[item.name] = item.options;
      }
    });
    map[menuName] = subMap;
  });
  return map;
};

const VALID_LABELS_MAP = buildValidLabelsMap();

const filterRightsAgainstConstants = (rights) => {
  if (!Array.isArray(rights)) return rights;
  return rights.map((menuRight) => {
    const menuLabelsMap = VALID_LABELS_MAP[menuRight.menu];
    return {
      ...menuRight,
      subRights: (menuRight.subRights || []).map((subRight) => ({
        ...subRight,
        permissions: (subRight.permissions || []).filter((perm) => {
          const validLabels = menuLabelsMap?.[subRight.subMenu];
          return !validLabels || validLabels.includes(perm.label);
        }),
      })),
    };
  });
};

const deduplicateRights = (rights) => {
  const seen = new Set();
  const result = [];
  for (let i = (rights || []).length - 1; i >= 0; i--) {
    if (!seen.has(rights[i].menu)) {
      seen.add(rights[i].menu);
      result.unshift(rights[i]);
    }
  }
  return result;
};

const normalizeSelectPermissionsFromServer = (selections) => {
  if (!selections || !Object.prototype.hasOwnProperty.call(selections, "Quotes")) {
    return selections;
  }
  const next = normalizeSelectQuotesPermissions(selections["Quotes"]);
  if ((selections["Quotes"] || []).join("|") === next.join("|")) {
    return selections;
  }
  return { ...selections, Quotes: next };
};

const NewRightsPage = () => {
  const { setIsRightPage, selectRoleBtnName, setSelectRoleBtnName, setEnableSaveRightsBtn, setSelectRoleOptions, saveRightModalOpen, setSaveRightModalOpen, setVibrateButton } = useSettingContext();
  const [standardUserState, setStandardUserState] = useState({ checkedState: {}, permissionSelection: {}, expanded: {} });
  const [cadUserState, setCadUserState] = useState({ checkedState: {}, permissionSelection: {}, expanded: {} });
  const [activeTab, setActiveTab] = useState("Vision");
  const [checkedState, setCheckedState] = useState({});
  const [permissionSelection, setPermissionSelection] = useState({});
  const [expanded, setExpanded] = useState({});
  const [allCollapsed, setAllCollapsed] = useState(false);
  const [savingRights, setSavingRights] = useState(false);
  const tabsFilter = ["My Binny's", "Track App"];
  const permission = usePermissions();
  const EditAccessNewRights = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Rights"), "edit");

  useRightsValidation(activeTab, permissionSelection, checkedState, setPermissionSelection, setCheckedState);
  const { searchTerm } = useSettingContext();
  const [tabSpecificState, setTabSpecificState] = useState({
    Vision: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
    Create: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
    "Scan App": { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
    "Track App": { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
    "My Binny's": { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
    Select: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
    Nexus: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
    Fab: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
    Breco: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
    Aira: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
    Expo: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
    Creco: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
    Techie: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
    "Client Master": { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
    Purchie: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
    CRM: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentPopoverItem, setCurrentPopoverItem] = useState(null);
  const [roleRights, setRoleRights] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const isRoleSelected = useMemo(() => selectRoleBtnName !== "Select Role" && selectRoleBtnName !== "", [selectRoleBtnName]);

  const [brecoGroups, setBrecoGroups] = useState([]);
  const [brecoGroupIds, setBrecoGroupIds] = useState([]);
  const [brecoGroupsExpanded, setBrecoGroupsExpanded] = useState(false);
  const savedBrecoGroupIds = useMemo(() => roleRights.find((item) => item.menu === "Breco")?.allowedGroupIds || [], [roleRights]);
  const brecoGroupsChanged = useMemo(
    () => [...savedBrecoGroupIds].sort().join("|") !== [...brecoGroupIds].sort().join("|"),
    [savedBrecoGroupIds, brecoGroupIds]
  );

  useEffect(() => {
    setBrecoGroupIds(savedBrecoGroupIds);
  }, [savedBrecoGroupIds]);

  useEffect(() => {
    if (activeTab !== "Breco" || brecoGroups.length) return;
    getBrecoClientGroups()
      .then((res) => setBrecoGroups((res?.groups || res?.data || []).map((group) => ({ id: group.id, name: group.name }))))
      .catch(() => toast.error("Failed to load Breco client groups"));
  }, [activeTab]);

  const handleBrecoGroupToggle = (groupId) => {
    setBrecoGroupIds((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
  };
  const [selectedUserType, setSelectedUserType] = React.useState("Standard User");
  const [userDropdownOpen, setUserDropdownOpen] = React.useState(false);

  const getExpandFields = () => {
    const isVision = activeTab === "Vision";
    const isScanApp = activeTab === "Scan App";
    const isNexus = activeTab === "Nexus";
    const isBreco = activeTab === "Breco";

    if (activeTab === "CRM") return CRM_PAGE_INFO;

    let sourceData = isVision ? VISION_PAGE_INFO : isScanApp ? SCAN_APP_PAGE_INFO : isNexus ? NEXUS_WEB_PAGE_INFO : isBreco ? BRECO_PAGE_INFO : CREATE_PAGE_INFO;

    let filteredSections;

    if (isVision) {
      filteredSections = sourceData.filter((item) => item.name !== "Sets Access");
    } else if (isScanApp || isNexus || isBreco) {
      filteredSections = sourceData.filter((item) => item.isMainMenu);
    } else {
      filteredSections = sourceData;
    }

    return filteredSections;
  };

  const handleToggleAll = () => {
    const filteredSections = getExpandFields();

    const updatedState = {};
    const newAllCollapsed = !allCollapsed;

    filteredSections.forEach((item) => {
      updatedState[item.name] = newAllCollapsed;
    });

    setExpanded(updatedState);
    setAllCollapsed(newAllCollapsed);

    setTabSpecificState((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        expanded: updatedState,
        allCollapsed: newAllCollapsed,
      },
    }));
  };

  const handleToggleExpand = (page) => {
    const filteredSections = getExpandFields();

    setExpanded((prev) => {
      const newState = { ...prev, [page]: !prev[page] };

      const allExpanded = filteredSections.every((item) => newState[item.name] === true);

      const allCollapsedNow = filteredSections.every((item) => newState[item.name] === false);
      let newAllCollapsed = allCollapsed;

      if (allExpanded) {
        newAllCollapsed = false;
      } else if (allCollapsedNow) {
        newAllCollapsed = true;
      } else {
        newAllCollapsed = false;
      }

      setAllCollapsed(newAllCollapsed);
      setTabSpecificState((prevTabState) => ({
        ...prevTabState,
        [activeTab]: {
          ...prevTabState[activeTab],
          expanded: newState,
          allCollapsed: newAllCollapsed,
        },
      }));

      return newState;
    });
  };

  const handleSaveRights = async () => {
    if (!roleRights || roleRights.length === 0) {
      toast.error("No role rights data available");
      return;
    }

    setSavingRights(true);

    try {
      // Create a copy of the original rights to modify
      let updatedRights = [...roleRights];

      // Track which tabs have changes for UI updates
      const changedTabs = [];

      for (const tab of TABS) {
        const tabState = tabSpecificState[tab];
        const tabHasChanges = tab === activeTab ? checkCurrentTabChanges() : tabState.hasChanges;
        if (!tabHasChanges) continue;

        changedTabs.push(tab);
        const tabMenus = TAB_TO_MENU_MAP[tab] || [];
        const tabCheckedState = tab === activeTab ? checkedState : tabState.checkedState;
        const tabTrackAppSelections = tab === activeTab ? permissionSelection : tabState.permissionSelection;

        // Update the rights for this tab
        updatedRights = updatedRights.map((menuItem) => {
          if (tabMenus?.includes(menuItem.menu)) {
            if (menuItem.subRights && Array.isArray(menuItem.subRights)) {
              const toggleConfig = TOGGLE_TAB_CONFIG[tab];
              if (toggleConfig && menuItem.menu === toggleConfig.menuName) {
                const accessEnabled = !!tabCheckedState[toggleConfig.toggleKey];

                return {
                  ...menuItem,
                  subRights: menuItem.subRights.map((subRight) => {
                    const subMenuName = subRight.subMenu;
                    if (subMenuName === toggleConfig.toggleKey) {
                      return {
                        ...subRight,
                        enabled: accessEnabled,
                        permissions: subRight.permissions || [],
                      };
                    }

                    const isEnabled = accessEnabled && !!tabCheckedState[subMenuName];
                    const selectedPermissions = tabTrackAppSelections[subMenuName] || [];

                    if (subRight.permissions && Array.isArray(subRight.permissions)) {
                      return {
                        ...subRight,
                        enabled: isEnabled,
                        permissions: subRight.permissions.map((permission) => ({
                          ...permission,
                          allowed: selectedPermissions.includes(permission.label),
                        })),
                      };
                    }

                    return {
                      ...subRight,
                      enabled: isEnabled,
                    };
                  }),
                };
              } else {
                return {
                  ...menuItem,
                  subRights: menuItem.subRights.map((subRight) => {
                    const subMenuName = subRight.subMenu;
                    let keyToRead = subMenuName;

                    if (!tabsFilter?.includes(tab) && tab !== "CRM") {
                      const info = tab === "Vision" ? VISION_PAGE_INFO : CREATE_PAGE_INFO;
                      const matchingKeys = [];
                      info.forEach((main) => {
                        main.subMenus?.forEach((sub) => {
                          if (sub.name === subMenuName) {
                            matchingKeys.push(`${main.name}_${subMenuName}`);
                          }
                        });
                      });

                      keyToRead = matchingKeys.length === 0 ? subMenuName : matchingKeys[0];
                    }

                    const isEnabled = !!tabCheckedState[keyToRead];
                    const selectedPermissions = tabTrackAppSelections[keyToRead] || [];

                    if (subRight.permissions && Array.isArray(subRight.permissions)) {
                      return {
                        ...subRight,
                        enabled: isEnabled,
                        permissions: subRight.permissions.map((permission) => ({
                          ...permission,
                          allowed: selectedPermissions.includes(permission.label),
                        })),
                      };
                    }

                    return {
                      ...subRight,
                      enabled: isEnabled,
                    };
                  }),
                };
              }
            }
          }

          return menuItem;
        });

        if (tab === "Breco") {
          updatedRights = updatedRights.map((menuItem) => (menuItem.menu === "Breco" ? { ...menuItem, allowedGroupIds: brecoGroupIds } : menuItem));
        }

        // Create > Data Access: inject sub-right for roles created before it existed
        if (tab === "Create") {
          const dataAccessKey = "Data Access_Data Access";
          updatedRights = updatedRights.map((menuItem) => {
            if (menuItem.menu !== "Create" || !Array.isArray(menuItem.subRights)) return menuItem;
            if (menuItem.subRights.some((subRight) => subRight.subMenu === "Data Access")) return menuItem;
            const selected = tabTrackAppSelections[dataAccessKey] || [];
            return {
              ...menuItem,
              subRights: [
                ...menuItem.subRights,
                {
                  subMenu: "Data Access",
                  enabled: !!tabCheckedState[dataAccessKey],
                  permissions: [
                    { key: "all_data", label: "All Data", allowed: selected.includes("All Data") },
                    { key: "mine_data", label: "Mine Data", allowed: selected.includes("Mine Data") },
                  ],
                },
              ],
            };
          });
        }

        // Nexus: inject sub-rights for pages added/renamed after the role was saved
        // (e.g. "Monthly (MTD)" -> "Monthly"), otherwise toggling them never persists
        if (tab === "Nexus") {
          const accessEnabled = !!tabCheckedState["Nexus Web Access"];
          const pageNames = NEXUS_WEB_PAGE_INFO.flatMap((item) => (item.subMenus || []).map((sub) => sub.name));
          updatedRights = updatedRights.map((menuItem) => {
            if (menuItem.menu !== "Nexus" || !Array.isArray(menuItem.subRights)) return menuItem;
            const existing = new Set(menuItem.subRights.map((subRight) => subRight.subMenu));
            const missing = pageNames.filter((name) => !existing.has(name));
            if (!missing.length) return menuItem;
            return {
              ...menuItem,
              subRights: [
                ...menuItem.subRights,
                ...missing.map((name) => {
                  const selected = tabTrackAppSelections[name] || [];
                  return {
                    subMenu: name,
                    enabled: accessEnabled && !!tabCheckedState[name],
                    permissions: [{ key: "view", label: "View", allowed: accessEnabled && selected.includes("View") }],
                  };
                }),
              ],
            };
          });
        }

        // If tab menu not in rights yet, build from default and inject
        const tabMenuExists = updatedRights.some((item) => tabMenus.includes(item.menu));
        if (!tabMenuExists && DEFAULT_RIGHTS_MAP[tab]) {
          const defaultRight = DEFAULT_RIGHTS_MAP[tab];
          const toggleConfig = TOGGLE_TAB_CONFIG[tab];
          const accessEnabled = toggleConfig ? !!tabCheckedState[toggleConfig.toggleKey] : false;
          const newRight = {
            ...defaultRight,
            ...(tab === "Breco" ? { allowedGroupIds: brecoGroupIds } : {}),
            subRights: defaultRight.subRights.map((subRight) => {
              const subMenuName = subRight.subMenu;
              if (toggleConfig && subMenuName === toggleConfig.toggleKey) {
                return { ...subRight, enabled: accessEnabled };
              }
              // Tabs without an app-access toggle (CRM, Techie): row state alone decides
              const isEnabled = (toggleConfig ? accessEnabled : true) && !!tabCheckedState[subMenuName];
              const selectedPermissions = tabTrackAppSelections[subMenuName] || [];
              if (subRight.permissions && Array.isArray(subRight.permissions)) {
                return {
                  ...subRight,
                  enabled: isEnabled,
                  permissions: subRight.permissions.map((p) => ({ ...p, allowed: selectedPermissions.includes(p.label) })),
                };
              }
              return { ...subRight, enabled: isEnabled };
            }),
          };
          updatedRights = [...updatedRights, newRight];
        }
      }

      // If no changes in any tab, don't save
      if (changedTabs.length === 0) {
        toast.info("No changes to save");
        setSavingRights(false);
        return;
      }

      // Make single API call with all rights changes
      const payload = {
        rights: deduplicateRights(updatedRights),
        roleName: selectRoleBtnName,
        createUserType: selectedUserType === "Standard User" ? "Standard" : "CAD",
      };

      const response = await saveRights(payload);

      if (response.status === 200) {
        // Reset all changed tabs
        const resetTabStates = { ...tabSpecificState };
        changedTabs.forEach((tab) => {
          if (resetTabStates[tab]) {
            resetTabStates[tab] = {
              ...resetTabStates[tab],
              hasChanges: false,
            };
          }
        });

        setTabSpecificState(resetTabStates);
        setEnableSaveRightsBtn(false);
        setSaveRightModalOpen(false);
        toast.success(`Successfully saved rights for: ${changedTabs.join(", ")}`, {
          autoClose: 2000,
          hideProgressBar: false,
        });

        // Refresh the role rights after saving
        try {
          const role = await getSpecificRole(selectRoleBtnName);
          setRoleRights(deduplicateRights(filterRightsAgainstConstants(role?.data?.rights)));
        } catch (refreshError) {
          console.error("Error refreshing role rights:", refreshError);
          toast.warning("Rights saved but failed to refresh data");
        }
      } else {
        toast.error(`Failed to save rights: ${response.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error in save process:", error);
      toast.error("An unexpected error occurred while saving rights");
    } finally {
      setSavingRights(false);
    }
  };

  useEffect(() => {
    setIsRightPage(true);
    setSelectRoleBtnName("Select Role");

    return () => {
      setIsRightPage(false);
      setSelectRoleOptions([]);
      setSelectRoleBtnName("");
      setEnableSaveRightsBtn(false);
      setVibrateButton(false);
    };
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const roles = await getAllRoles({ search: searchTerm });
        setSelectRoleOptions(roles?.data);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    fetchRoles();
  }, [searchTerm]);

  useEffect(() => {
    if (selectRoleBtnName === "Select Role" || selectRoleBtnName === "") return;

    const fetchSpecificRole = async () => {
      try {
        const role = await getSpecificRole(selectRoleBtnName);

        setRoleRights(deduplicateRights(filterRightsAgainstConstants(role?.data?.rights)));
        setIsAdmin(role?.data?.isAdmin);
        setSelectedUserType(`${role?.data?.createUserType || "Standard"} User`);

        const originalStates = {};
        TABS.forEach((tab) => {
          originalStates[tab] = {
            checkedState: {},
            permissionSelection: {},
            expanded: {},
            allCollapsed: false,
          };
        });

        const initialTabState = {
          Vision: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
          Create: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
          "Scan App": { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
          "Track App": { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
          "My Binny's": { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
          Select: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
          Nexus: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
          Fab: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
          Breco: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
          Aira: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
          Expo: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
          Creco: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
          Techie: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
          "Client Master": { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
          Purchie: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
          CRM: { checkedState: {}, permissionSelection: {}, expanded: {}, allCollapsed: false, hasChanges: false },
        };
        setTabSpecificState(initialTabState);
        setIsInitialLoad(true);
      } catch (error) {
        console.error("Error fetching specific role:", error);
        setRoleRights([]);
      }
    };

    fetchSpecificRole();
  }, [selectRoleBtnName]);

  const checkCurrentTabChanges = () => {
    if (!roleRights?.length) return false;

    if (activeTab === "Breco" && brecoGroupsChanged) return true;

    const currentTabMenus = TAB_TO_MENU_MAP[activeTab] || [];
    const menusToCheck = roleRights.filter((item) => currentTabMenus.includes(item.menu));

    if (menusToCheck.length === 0) {
      return Object.keys(permissionSelection).length > 0 || Object.values(checkedState).some(Boolean);
    }

    // Create > Data Access missing on role: any UI selection counts as a change
    if (activeTab === "Create") {
      const hasDataAccessRight = menusToCheck.some((menu) => menu.subRights?.some((subRight) => subRight.subMenu === "Data Access"));
      if (!hasDataAccessRight && checkedState["Data Access_Data Access"]) return true;
    }

    // Nexus page missing on role (added/renamed later): any UI selection counts as a change
    if (activeTab === "Nexus") {
      const existing = new Set(menusToCheck.flatMap((menu) => (menu.subRights || []).map((subRight) => subRight.subMenu)));
      const missingSelected = NEXUS_WEB_PAGE_INFO.some((item) =>
        (item.subMenus || []).some((sub) => !existing.has(sub.name) && (checkedState[sub.name] || permissionSelection[sub.name]?.length))
      );
      if (missingSelected) return true;
    }

    if (TOGGLE_TAB_CONFIG[activeTab]) {
      const { menuName, toggleKey } = TOGGLE_TAB_CONFIG[activeTab];
      const AppAccessEnabled = !!checkedState[toggleKey];

      for (const menuItem of menusToCheck) {
        if (menuItem.menu === menuName && menuItem.subRights && Array.isArray(menuItem.subRights)) {
          for (const subRight of menuItem.subRights) {
            const subMenuName = subRight.subMenu;

            if (subMenuName === toggleKey) {
              if (subRight.enabled !== AppAccessEnabled) {
                return true;
              }
            } else {
              const uiEnabled = AppAccessEnabled && !!checkedState[subMenuName];

              if (subRight.enabled !== uiEnabled) {
                return true;
              }

              if (subRight.permissions && Array.isArray(subRight.permissions)) {
                const uiPermissions = permissionSelection[subMenuName] || [];

                const hasPermissionChanged = subRight.permissions.some((permission) => {
                  const uiHasPermission = uiPermissions.includes(permission.label);
                  return permission.allowed !== uiHasPermission;
                });

                if (hasPermissionChanged) {
                  return true;
                }
              }
            }
          }
        }
      }
    } else {
      const subMenuCounts = {};

      for (const menuItem of menusToCheck) {
        if (menuItem.subRights && Array.isArray(menuItem.subRights)) {
          for (const subRight of menuItem.subRights) {
            const subMenuName = subRight.subMenu;
            let keyToRead = subMenuName;

            if (activeTab !== "Select") {
              if (!tabsFilter?.includes(activeTab) && activeTab !== "CRM") {
                const info = sectionObject?.[activeTab] || [];

                const matchingKeys = [];
                info.forEach((main) => {
                  main.subMenus?.forEach((sub) => {
                    if (sub.name === subMenuName) {
                      matchingKeys.push(`${main.name}_${subMenuName}`);
                    }
                  });
                });

                if (matchingKeys.length === 0) {
                  keyToRead = subMenuName;
                } else if (matchingKeys.length === 1) {
                  keyToRead = matchingKeys[0];
                } else {
                  subMenuCounts[subMenuName] = subMenuCounts[subMenuName] || 0;
                  keyToRead = matchingKeys[subMenuCounts[subMenuName]];
                  subMenuCounts[subMenuName]++;
                }
              }
            }

            const uiEnabled = !!checkedState[keyToRead];

            if (subRight.enabled !== uiEnabled) {
              return true;
            }

            if (subRight.permissions && Array.isArray(subRight.permissions)) {
              const uiPermissions = permissionSelection[keyToRead] || [];

              const hasPermissionChanged = subRight.permissions.some((permission) => {
                const uiHasPermission = uiPermissions.includes(permission.label);
                return permission.allowed !== uiHasPermission;
              });

              if (hasPermissionChanged) {
                return true;
              }
            }
          }
        }
      }
    }

    return false;
  };

  // Track changes across all tabs
  useEffect(() => {
    if (!isRoleSelected) {
      setEnableSaveRightsBtn(false);
      return;
    }

    let anyTabHasChanges = false;
    TABS.forEach((tab) => {
      if (tabSpecificState[tab]?.hasChanges) {
        anyTabHasChanges = true;
      }
    });

    setEnableSaveRightsBtn(anyTabHasChanges);
  }, [tabSpecificState, isRoleSelected]);

  // Update hasChanges for current tab when state changes
  useEffect(() => {
    if (isInitialLoad || !roleRights?.length) return;

    const hasChanges = checkCurrentTabChanges();

    setTabSpecificState((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        checkedState,
        permissionSelection,
        expanded,
        allCollapsed,
        hasChanges,
      },
    }));
  }, [checkedState, permissionSelection, expanded, allCollapsed, activeTab, roleRights, isInitialLoad, brecoGroupsChanged]);

  // Initialize tab state from roleRights
  useEffect(() => {
    if (!roleRights?.length) {
      if (isInitialLoad) {
        setCheckedState({});
        setPermissionSelection({});
      }
      return;
    }
    const currentTabSavedState = tabSpecificState[activeTab];
    if (currentTabSavedState?.hasChanges && !isInitialLoad) {
      return;
    }

    const currentTabMenus = TAB_TO_MENU_MAP[activeTab] || [];
    const menusToProcess = roleRights.filter((item) => currentTabMenus.includes(item.menu));

    if (!menusToProcess.length) {
      if (isInitialLoad) {
        setCheckedState({});
        setPermissionSelection({});
        setIsInitialLoad(false);
      }
      return;
    }

    const enabledSubMenus = {};
    const enabledSubMenusWithPermissions = {};

    if (TOGGLE_TAB_CONFIG[activeTab]) {
      const { menuName } = TOGGLE_TAB_CONFIG[activeTab];
      menusToProcess.forEach((menuItem) => {
        if (menuItem.menu === menuName && menuItem.subRights?.length) {
          menuItem.subRights.forEach((subRight) => {
            const subMenuName = subRight.subMenu;

            if (subRight.enabled) {
              enabledSubMenus[subMenuName] = true;

              if (subRight.permissions?.length) {
                const allowedPermissions = subRight.permissions.filter((permission) => permission.allowed).map((permission) => permission.label);

                if (allowedPermissions.length) {
                  enabledSubMenusWithPermissions[subMenuName] = allowedPermissions;
                }
              }
            }
          });
        }
      });
    } else {
      const subMenuCounts = {};
      const pageInfo = getPageInfoForTab(activeTab);

      menusToProcess.forEach((menuItem) => {
        if (!menuItem.subRights?.length) return;

        menuItem.subRights.forEach((subRight) => {
          const subMenuName = subRight.subMenu;
          let keyToSet = subMenuName;
          if (activeTab !== "Select") {
            keyToSet = generateSubMenuKey(subMenuName, activeTab, pageInfo, subMenuCounts);
          }

          if (!subRight.enabled) return;
          enabledSubMenus[keyToSet] = true;
          if (subRight.permissions?.length) {
            const allowedPermissions = subRight.permissions.filter((permission) => permission.allowed).map((permission) => permission.label);

            if (allowedPermissions.length) {
              enabledSubMenusWithPermissions[keyToSet] = allowedPermissions;
            }
          }
        });
      });
    }

    const permissionsToApply = activeTab === "Select" ? normalizeSelectPermissionsFromServer(enabledSubMenusWithPermissions) || enabledSubMenusWithPermissions : enabledSubMenusWithPermissions;

    setCheckedState(enabledSubMenus);
    setPermissionSelection(permissionsToApply);
    setIsInitialLoad(false);
  }, [roleRights, activeTab]);

  const getPageInfoForTab = (tab) => {
    switch (tab) {
      case "Vision":
        return VISION_PAGE_INFO;
      case "Create":
        return CREATE_PAGE_INFO;
      case "Select":
        return SELECT_PAGE_INFO;
      default:
        return null;
    }
  };

  const generateSubMenuKey = (subMenuName, activeTab, pageInfo, countMap) => {
    if (activeTab === "Select") {
      return subMenuName;
    }

    if (activeTab !== "Vision" && activeTab !== "Create") {
      return subMenuName;
    }

    const matchingKeys = [];

    pageInfo.forEach((main) => {
      main.subMenus?.forEach((sub) => {
        if (sub.name === subMenuName) {
          matchingKeys.push(`${main.name}_${subMenuName}`);
        }
      });
    });

    if (matchingKeys.length === 0) {
      return subMenuName;
    }

    if (matchingKeys.length === 1) {
      return matchingKeys[0];
    }

    countMap[subMenuName] = countMap[subMenuName] || 0;
    const index = countMap[subMenuName];
    countMap[subMenuName] = (countMap[subMenuName] + 1) % matchingKeys.length;

    return matchingKeys[index];
  };
  const handleSwitchChange = (page, options = [], isMainItem = false) => {
    if (!isRoleSelected) {
      setVibrateButton(true);
      return;
    }

    // CRM: child rights stay locked until their parent right is ON
    if (activeTab === "CRM" && !isCrmParentOn(page, checkedState)) {
      setVibrateButton(true);
      return;
    }

    // Define Access fields that should never be disabled
    const accessFields =["Catalog_Catalog Access", "Stocks_Stocks Access", "Collection_Collection Access", "Category_Category Access"];

    if (activeTab === "Create" && selectedUserType === "CAD User") {
      const cadEnabledMenus = ["CAD", "CAM", "Sizing", "Modification", "Miscellaneous Settings"];

      if (isMainItem) {
        if (!cadEnabledMenus.includes(page)) {
          setVibrateButton(true);
          return;
        }
      } else if (page.includes("_")) {
        const parentMenu = page.split("_")[0];
        if (!cadEnabledMenus.includes(parentMenu)) {
          setVibrateButton(true);
          return;
        }

        const subMenuName = page.split("_")[1];
        const isAccessItem = subMenuName?.endsWith("Access");
        const isCadComments = parentMenu === "Miscellaneous Settings" && subMenuName === "Comments";

        if (!isAccessItem && !isCadComments) {
          setVibrateButton(true);
          return;
        }
      }
    }

    if (activeTab === "Select") {
      const hasBinnysView = permissionSelection["Binny's Select Access"]?.includes("View");

      if ((page === "Quotes" || page === "Share Access") && !hasBinnysView) {
        setVibrateButton(true);
        return;
      }
    }

    if (activeTab === "Create" && !isMainItem && page.includes("_")) {
      const mainKey = page.split("_")[0];
      const accessKey = `${mainKey}_${mainKey} Access`;

      const isActionItem = page.includes("Actions") || page.includes("Comments") || page.includes("Item Images");

      if (isActionItem) {
        const hasParentAccess = CREATE_PAGE_INFO.some((menu) => menu.name === mainKey && menu.subMenus?.some((sub) => sub.name === `${mainKey} Access`));
        if (hasParentAccess) {
          const hasAccessView = checkedState[accessKey] && permissionSelection[accessKey]?.includes("View");
          if (!hasAccessView) {
            setVibrateButton(true);
            return;
          }
        }
      }
    }

    if (activeTab === "Vision" && !isMainItem && page.includes("_")) {
      // Skip validation for Access fields themselves - don't disable them
      if (!accessFields.includes(page)) {
        const mainKey = page.split("_")[0];
        let viewKey = "";

        if (mainKey === "Catalog") viewKey = "Catalog_Catalog Access";
        else if (mainKey === "Stocks") viewKey = "Stocks_Stocks Access";
        else if (mainKey === "Collection") viewKey = "Collection_Collection Access";
        else if (mainKey === "Category") viewKey = "Category_Category Access";

        if (viewKey) {
          const hasView = checkedState[viewKey] && permissionSelection[viewKey]?.includes("View");
          if (!hasView) {
            setVibrateButton(true);
            return;
          }
        }
      }
    }

    if (activeTab === "My Binny's" && (page === "User Management" || page === "Report Management")) {
      const hasClientManagementView = checkedState["Client Management"] && permissionSelection["Client Management"]?.includes("View");
      if (!hasClientManagementView) {
        setVibrateButton(true);
        return;
      }
    }

    // Track App - Dues and Ledger have no backend validation (like Vision)
    // They toggle freely without any dependency checks

    if (activeTab === "Select") {
      setCheckedState((prevChecked) => {
        const isChecked = !prevChecked[page];
        let nextChecked = { ...prevChecked, [page]: isChecked };

        setPermissionSelection((prevSel) => {
          const restrictedOptions = getRestrictedOptionsForItem(page);
          let nextSel = { ...prevSel };

          if (isChecked && options?.length) {
            if (page === "Quotes") {
              nextSel[page] = ["All"];
            } else if (page === "Orders") {
              nextSel[page] = options.filter((opt) => !restrictedOptions.includes(opt));
            } else {
              const defaultOptions = options.filter((opt) => opt !== "");
              nextSel[page] = defaultOptions.filter((opt) => !restrictedOptions.includes(opt));
            }
            if (page === "Binny's Select Access") {
              nextChecked["Is Stock Manager"] = false;
              delete nextSel["Is Stock Manager"];
            }
            if (page === "Is Stock Manager") {
              ["Binny's Select Access", "Quotes", "Share Access"].forEach((k) => {
                nextChecked[k] = false;
                delete nextSel[k];
              });
            }
          } else {
            delete nextSel[page];
            if (page === "Binny's Select Access") {
              ["Quotes", "Share Access"].forEach((k) => {
                nextChecked[k] = false;
                delete nextSel[k];
              });
            }
            if (isMainItem) {
              Object.keys(nextSel).forEach((itemKey) => {
                if (itemKey.startsWith(`${page}_`)) {
                  delete nextSel[itemKey];
                }
              });
            }
          }

          return nextSel;
        });

        if (!isChecked && isMainItem) {
          Object.keys(nextChecked).forEach((itemKey) => {
            if (itemKey.startsWith(`${page}_`)) {
              delete nextChecked[itemKey];
            }
          });
        }

        return nextChecked;
      });
      return;
    }

    setCheckedState((prev) => {
      const isChecked = !prev[page];
      const newCheckedState = { ...prev, [page]: isChecked };

      if (options && options.length > 0) {
        setPermissionSelection((prevSelections) => {
          const newSelections = { ...prevSelections };
          const restrictedOptions = getRestrictedOptionsForItem(page);

          if (isChecked) {
            if (SINGLE_SELECT_ITEMS[page]) {
              newSelections[page] = [SINGLE_SELECT_ITEMS[page]];
            } else if (page === "Orders") {
              newSelections[page] = options.filter((opt) => !restrictedOptions.includes(opt));
            } else {
              const defaultOptions = options.filter((opt) => opt !== "");
              newSelections[page] = defaultOptions.filter((opt) => !restrictedOptions.includes(opt));
            }
          } else {
            delete newSelections[page];

            if (isMainItem) {
              Object.keys(newSelections).forEach((itemKey) => {
                if (itemKey.startsWith(`${page}_`)) {
                  delete newSelections[itemKey];
                }
              });
            }
          }

          return newSelections;
        });
      }

      if (!isChecked && isMainItem) {
        Object.keys(newCheckedState).forEach((itemKey) => {
          if (itemKey.startsWith(`${page}_`)) {
            delete newCheckedState[itemKey];
          }
        });
      }

      return newCheckedState;
    });
  };

  const handleOptionToggle = (itemName, option) => {
    const restrictedOptions = getRestrictedOptionsForItem(itemName);
    if (restrictedOptions.includes(option)) return;

    setPermissionSelection((prev) => {
      const currentSelected = prev[itemName] || [];
      const isAICollection = itemName === "Collection_AI Collection";

      let allOptions = [];
      if (isAICollection) {
        for (const mainItem of VISION_PAGE_INFO) {
          for (const subItem of mainItem.subMenus) {
            const itemKey = `${mainItem.name}_${subItem.name}`;
            if (itemKey === itemName || mainItem.name === itemName) {
              allOptions = subItem.options || mainItem.options || [];
            }
          }
        }
      }

      const filteredAllOptions = allOptions.filter((opt) => opt !== "None" && opt !== "All");

      let updated = [];
      if (isAICollection && option === "All") {
        const isAllSelected = currentSelected.includes("All") || currentSelected.length === filteredAllOptions.length;

        updated = isAllSelected ? [] : ["All", ...filteredAllOptions];
      } else if (isAICollection) {
        if (currentSelected.includes(option)) {
          updated = currentSelected.filter((o) => o !== option);
          updated = updated.filter((o) => o !== "All");
        } else {
          updated = [...currentSelected, option];

          if (updated.length === filteredAllOptions.length) {
            updated = ["All", ...filteredAllOptions];
          }
        }
      } else if (SINGLE_SELECT_ITEMS[itemName]) {
        updated = currentSelected.includes(option) ? [] : [option];
      } else if (activeTab === "Select" && itemName === "Quotes" && ["All", "Own", "None"].includes(option)) {
        updated = currentSelected.includes(option) ? [] : [option];
      } else {
        updated = currentSelected.includes(option) ? currentSelected.filter((o) => o !== option) : [...currentSelected, option];
      }

      let nextPermission = { ...prev, [itemName]: updated };

      if (activeTab === "Select" && Object.prototype.hasOwnProperty.call(nextPermission, "Quotes")) {
        nextPermission = {
          ...nextPermission,
          Quotes: normalizeSelectQuotesPermissions(nextPermission["Quotes"]),
        };
      }

      if (activeTab === "Select" && itemName === "Binny's Select Access") {
        if (updated.includes("View")) {
          delete nextPermission["Is Stock Manager"];
        } else {
          delete nextPermission["Quotes"];
          delete nextPermission["Share Access"];
        }
      }
      if (activeTab === "Select" && itemName === "Is Stock Manager" && updated.includes("View")) {
        delete nextPermission["Binny's Select Access"];
        delete nextPermission["Quotes"];
        delete nextPermission["Share Access"];
      }

      if (activeTab === "Purchie") {
        const hasWaitingOrDecided = (nextPermission["Approvals"] || []).some((o) => o === "Waiting For You" || o === "Already Decided");
        if (!hasWaitingOrDecided) {
          nextPermission["Approvals"] = (nextPermission["Approvals"] || []).filter((o) => o !== "Date Filter" && o !== "Year Change");
        }
        if (!(nextPermission["Approvals"] || []).includes("Date Filter")) {
          nextPermission["Approvals"] = (nextPermission["Approvals"] || []).filter((o) => o !== "Year Change");
        }

        const hasReportOrMismatch = (nextPermission["Goods Received"] || []).some((o) => o === "Reports" || o === "Mismatch Report");
        if (!hasReportOrMismatch) {
          nextPermission["Goods Received"] = (nextPermission["Goods Received"] || []).filter((o) => o !== "Date Filter" && o !== "Year Change");
        }
        if (!(nextPermission["Goods Received"] || []).includes("Date Filter")) {
          nextPermission["Goods Received"] = (nextPermission["Goods Received"] || []).filter((o) => o !== "Year Change");
        }

        const hasVendorBase = (nextPermission["Vendors"] || []).some((o) => o === "All Vendors" || o === "Vendor Master");
        if (!hasVendorBase) {
          nextPermission["Vendors"] = (nextPermission["Vendors"] || []).filter((o) => o === "All Vendors" || o === "Vendor Master");
        }

        if (!(nextPermission["Items"] || []).includes("View")) {
          nextPermission["Items"] = [];
        }

        if (!(nextPermission["Stock"] || []).includes("View")) {
          nextPermission["Stock"] = [];
        }

        if (!(nextPermission["Upload Images"] || []).includes("View")) {
          nextPermission["Upload Images"] = [];
        }

        if (!(nextPermission["Reports"] || []).includes("Transaction Report")) {
          nextPermission["Reports"] = (nextPermission["Reports"] || []).filter((o) => o !== "Date Filter" && o !== "Year Change");
        }
        if (!(nextPermission["Reports"] || []).includes("Date Filter")) {
          nextPermission["Reports"] = (nextPermission["Reports"] || []).filter((o) => o !== "Year Change");
        }

        if (!(nextPermission["Settings"] || []).includes("Items Rate")) {
          nextPermission["Settings"] = (nextPermission["Settings"] || []).filter((o) => o !== "Download Excel");
        }
      }

      setCheckedState((prevChecked) => {
        let newCheckedState = { ...prevChecked, [itemName]: updated.length > 0 };
        if (activeTab === "Purchie") {
          newCheckedState["Approvals"] = (nextPermission["Approvals"] || []).length > 0;
          newCheckedState["Goods Received"] = (nextPermission["Goods Received"] || []).length > 0;
          newCheckedState["Vendors"] = (nextPermission["Vendors"] || []).length > 0;
          newCheckedState["Items"] = (nextPermission["Items"] || []).length > 0;
          newCheckedState["Stock"] = (nextPermission["Stock"] || []).length > 0;
          newCheckedState["Upload Images"] = (nextPermission["Upload Images"] || []).length > 0;
          newCheckedState["Reports"] = (nextPermission["Reports"] || []).length > 0;
          newCheckedState["Settings"] = (nextPermission["Settings"] || []).length > 0;
        }
        if (activeTab === "Select" && Object.prototype.hasOwnProperty.call(nextPermission, "Quotes")) {
          newCheckedState["Quotes"] = (nextPermission["Quotes"] || []).length > 0;
        }
        if (activeTab === "Select" && itemName === "Binny's Select Access") {
          if (updated.includes("View")) {
            newCheckedState["Is Stock Manager"] = false;
          } else {
            newCheckedState["Quotes"] = false;
            newCheckedState["Share Access"] = false;
          }
        }
        if (activeTab === "Select" && itemName === "Is Stock Manager" && updated.includes("View")) {
          newCheckedState["Binny's Select Access"] = false;
          newCheckedState["Quotes"] = false;
          newCheckedState["Share Access"] = false;
        }
        setTabSpecificState((prevTabState) => ({
          ...prevTabState,
          [activeTab]: {
            ...prevTabState[activeTab],
            permissionSelection: nextPermission,
            checkedState: newCheckedState,
          },
        }));
        return newCheckedState;
      });
      return nextPermission;
    });
  };

  const handlePopoverOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setCurrentPopoverItem(item);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setCurrentPopoverItem(null);
  };

  const handleDiscardRights = () => {
    setSaveRightModalOpen(false);
    if (roleRights && roleRights.length > 0) {
      const currentTabMenus = TAB_TO_MENU_MAP[activeTab] || [];
      const menusToProcess = roleRights.filter((item) => currentTabMenus.includes(item.menu));

      const enabledSubMenus = {};
      const enabledSubMenusWithPermissions = {};

      if (TOGGLE_TAB_CONFIG[activeTab]) {
        const { menuName } = TOGGLE_TAB_CONFIG[activeTab];
        menusToProcess.forEach((menuItem) => {
          if (menuItem.menu === menuName && menuItem.subRights?.length) {
            menuItem.subRights.forEach((subRight) => {
              const subMenuName = subRight.subMenu;
              if (subRight.enabled) {
                enabledSubMenus[subMenuName] = true;
                if (subRight.permissions?.length) {
                  enabledSubMenusWithPermissions[subMenuName] = subRight.permissions.filter((permission) => permission.allowed).map((permission) => permission.label);
                }
              }
            });
          }
        });
      } else if (activeTab === "Track App" || activeTab === "My Binny's" || activeTab === "Select" || activeTab === "CRM") {
        // Handle flat structure tabs
        menusToProcess.forEach((menuItem) => {
          if (menuItem.subRights && Array.isArray(menuItem.subRights)) {
            menuItem.subRights.forEach((subRight) => {
              const subMenuName = subRight.subMenu;
              if (subRight.enabled) {
                enabledSubMenus[subMenuName] = true;
                if (subRight.permissions?.length) {
                  enabledSubMenusWithPermissions[subMenuName] = subRight.permissions.filter((permission) => permission.allowed).map((permission) => permission.label);
                }
              }
            });
          }
        });
      } else {
        // Handle Vision and Create tabs (hierarchical with sectionObject)
        const subMenuCounts = {};
        const info = sectionObject?.[activeTab];

        menusToProcess.forEach((menuItem) => {
          if (menuItem.subRights && Array.isArray(menuItem.subRights)) {
            menuItem.subRights.forEach((subRight) => {
              const subMenuName = subRight.subMenu;
              let keyToSet = subMenuName;

              if (info) {
                const matchingKeys = [];
                info.forEach((main) => {
                  main.subMenus?.forEach((sub) => {
                    if (sub.name === subMenuName) {
                      matchingKeys.push(`${main.name}_${subMenuName}`);
                    }
                  });
                });

                if (matchingKeys.length === 0) {
                  keyToSet = subMenuName;
                } else if (matchingKeys.length === 1) {
                  keyToSet = matchingKeys[0];
                } else {
                  subMenuCounts[subMenuName] = subMenuCounts[subMenuName] || 0;
                  keyToSet = matchingKeys[subMenuCounts[subMenuName]];
                  subMenuCounts[subMenuName]++;
                }
              }

              if (subRight.enabled) {
                enabledSubMenus[keyToSet] = true;
                if (subRight.permissions?.length) {
                  enabledSubMenusWithPermissions[keyToSet] = subRight.permissions.filter((permission) => permission.allowed).map((permission) => permission.label);
                }
              }
            });
          }
        });
      }

      const discardPermissions = activeTab === "Select" ? normalizeSelectPermissionsFromServer(enabledSubMenusWithPermissions) || enabledSubMenusWithPermissions : enabledSubMenusWithPermissions;

      setCheckedState(enabledSubMenus);
      setPermissionSelection(discardPermissions);

      // Reset hasChanges to false for current tab
      setTabSpecificState((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          checkedState: enabledSubMenus,
          permissionSelection: discardPermissions,
          hasChanges: false,
        },
      }));
    }
  };

  useEffect(() => {
    if (!tabsFilter?.includes(activeTab)) return;

    const itemsToValidate = ["Dues", "Ledger"];
    let hasChanges = false;

    const newTrackAppSelections = { ...permissionSelection };
    const newCheckedState = { ...checkedState };

    itemsToValidate.forEach((itemName) => {
      const selectedOptions = permissionSelection[itemName] || [];
      const hasShareOrDownload = selectedOptions.some((opt) => opt === "Share" || opt === "Download");
      const hasCustomerOrSupplier = selectedOptions.some((opt) => opt === "Customers" || opt === "Suppliers");

      if (hasShareOrDownload && !hasCustomerOrSupplier) {
        delete newTrackAppSelections[itemName];

        newCheckedState[itemName] = false;

        hasChanges = true;
      }
    });
    if (hasChanges) {
      setPermissionSelection(newTrackAppSelections);
      setCheckedState(newCheckedState);
    }
  }, [permissionSelection, activeTab]);

  // CRM: parent turned OFF -> switch its children OFF too (cascades through chains)
  useEffect(() => {
    if (activeTab !== "CRM") return;
    const nextChecked = { ...checkedState };
    let changed = false;
    let loop = true;
    while (loop) {
      loop = false;
      for (const name of CRM_ROW_NAMES) {
        if (nextChecked[name] && !isCrmParentOn(name, nextChecked)) {
          nextChecked[name] = false;
          changed = true;
          loop = true;
        }
      }
    }
    if (!changed) return;
    setCheckedState(nextChecked);
    setPermissionSelection((prev) => {
      const next = { ...prev };
      CRM_ROW_NAMES.forEach((name) => {
        if (!nextChecked[name]) delete next[name];
      });
      return next;
    });
  }, [checkedState, activeTab]);

  // Unsaved changes: confirm before leaving / reloading the page
  const hasUnsavedChanges = useMemo(() => isRoleSelected && TABS.some((tab) => tabSpecificState[tab]?.hasChanges), [isRoleSelected, tabSpecificState]);
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsavedChanges]);

  const modalBtnInfo = [
    { btnName: "Discard Rights", bgColor: "#E77B7B", click: handleDiscardRights },
    {
      btnName: "Save Rights",
      bgColor: "#50C878",
      click: () => {
        if (savingRights) return;
        handleSaveRights();
      },
    },
  ];

  const renderUserTypeSelector = () => {
    const userTypes = ["Standard User", "CAD User"];

    const handleSelect = (type) => {
      if (isAdmin) return;
      if (selectedUserType === "Standard User") {
        setStandardUserState({
          checkedState,
          permissionSelection,
          expanded,
        });
      } else if (selectedUserType === "CAD User") {
        setCadUserState({
          checkedState,
          permissionSelection,
          expanded,
        });
      }
      const newState = type === "Standard User" ? standardUserState : cadUserState;

      setSelectedUserType(type);
      setCheckedState(newState.checkedState);
      setPermissionSelection(newState.permissionSelection);
      setExpanded(newState.expanded);
      setUserDropdownOpen(false);
    };

    const filteredTypes = userTypes.filter((type) => type !== selectedUserType);

    return (
      <Box sx={{ mb: 3, position: "relative", width: 260 }}>
        <Box
          onClick={() => setUserDropdownOpen(!userDropdownOpen)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            cursor: "pointer",
            color: "#fff",
            fontFamily: "Karla, sans-serif",
            fontWeight: 600,
            fontSize: "22px",
            lineHeight: 1.2,
          }}
        >
          {selectedUserType}
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 20,
              transform: userDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "0.2s ease",
            }}
          />
        </Box>

        {userDropdownOpen && !isAdmin && (
          <Box
            sx={{
              position: "absolute",
              top: "36px",
              left: 0,
              width: "230px",
              zIndex: 9999,
            }}
          >
            {filteredTypes.map((type) => (
              <Box
                key={type}
                onClick={() => handleSelect(type)}
                sx={{
                  width: "210px",
                  height: "48px",
                  backgroundColor: "#575757",
                  border: "4px solid #e6e6e6",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "Karla, sans-serif",
                  fontSize: "18px",
                  fontWeight: 500,
                  color: "#fff",
                  cursor: "pointer",
                  boxShadow: "0 6px 14px rgba(0,0,0,0.7)",
                }}
              >
                {type}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const handleTabSwitch = (tab) => {
    // Save current tab state before switching
    setTabSpecificState((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        checkedState,
        permissionSelection,
        expanded,
        allCollapsed,
      },
    }));

    // Load the new tab's state
    const newTabState = tabSpecificState[tab];
    setActiveTab(tab);
    setCheckedState(newTabState.checkedState);
    setPermissionSelection(tab === "Select" ? normalizeSelectPermissionsFromServer({ ...newTabState.permissionSelection }) || newTabState.permissionSelection : newTabState.permissionSelection);
    setExpanded(newTabState.expanded);
    setAllCollapsed(newTabState.allCollapsed);
  };

  const isCatalogAccessManagementEnabled = !!checkedState["Catalog_Catalog Access Management"] && (permissionSelection["Catalog_Catalog Access Management"] || []).length > 0;

  const catalogRestrictedOptions = useMemo(
    () => ({
      "Catalog_Miscellaneous Catalog": ["Session History", "Renew/Revoke Access", "Reshare Link", "Edit Mobile Number of Links"],
      Catalog_Reports: ["Client Reports"],
    }),
    []
  );

  const getRestrictedOptionsForItem = (itemKey) => {
    if (activeTab === "Create" && selectedUserType === "Standard User" && itemKey === "CAD_CAD Access") {
      return ["Old Cad Images"];
    }
    if (activeTab !== "Vision" || isCatalogAccessManagementEnabled) return [];
    return catalogRestrictedOptions[itemKey] || [];
  };

  const removedRestrictedSelectionsRef = useRef({
    misc: [],
    reports: [],
  });

  useEffect(() => {
    if (activeTab !== "Vision" || isCatalogAccessManagementEnabled) return;

    const restrictedMiscOptions = catalogRestrictedOptions["Catalog_Miscellaneous Catalog"] || [];
    const prevMisc = permissionSelection["Catalog_Miscellaneous Catalog"] || [];
    const prevReports = permissionSelection["Catalog_Reports"] || [];

    const nextMisc = prevMisc.filter((opt) => !restrictedMiscOptions.includes(opt));
    const nextReports = prevReports.filter((opt) => opt !== "Client Reports");
    const removedMisc = prevMisc.filter((opt) => restrictedMiscOptions.includes(opt));
    const removedReports = prevReports.filter((opt) => opt === "Client Reports");

    const miscChanged = nextMisc.length !== prevMisc.length;
    const reportsChanged = nextReports.length !== prevReports.length;

    if (!miscChanged && !reportsChanged) return;

    removedRestrictedSelectionsRef.current = {
      misc: removedMisc,
      reports: removedReports,
    };

    setPermissionSelection((prev) => {
      const next = { ...prev };
      if (miscChanged) next["Catalog_Miscellaneous Catalog"] = nextMisc;
      if (reportsChanged) next["Catalog_Reports"] = nextReports;
      return next;
    });

    setCheckedState((prev) => {
      const next = { ...prev };
      if (miscChanged) next["Catalog_Miscellaneous Catalog"] = nextMisc.length > 0;
      if (reportsChanged) next["Catalog_Reports"] = nextReports.length > 0;
      return next;
    });
  }, [activeTab, isCatalogAccessManagementEnabled, permissionSelection, catalogRestrictedOptions]);

  useEffect(() => {
    if (activeTab !== "Vision" || !isCatalogAccessManagementEnabled) return;

    const cachedMisc = removedRestrictedSelectionsRef.current.misc || [];
    const cachedReports = removedRestrictedSelectionsRef.current.reports || [];

    if (!cachedMisc.length && !cachedReports.length) return;

    setPermissionSelection((prev) => {
      const next = { ...prev };
      const currentMisc = prev["Catalog_Miscellaneous Catalog"] || [];
      const currentReports = prev["Catalog_Reports"] || [];

      const restoredMisc = [...new Set([...currentMisc, ...cachedMisc])];
      const restoredReports = [...new Set([...currentReports, ...cachedReports])];

      next["Catalog_Miscellaneous Catalog"] = restoredMisc;
      next["Catalog_Reports"] = restoredReports;
      return next;
    });

    setCheckedState((prev) => ({
      ...prev,
      "Catalog_Miscellaneous Catalog": true,
      Catalog_Reports: true,
    }));

    removedRestrictedSelectionsRef.current = {
      misc: [],
      reports: [],
    };
  }, [activeTab, isCatalogAccessManagementEnabled]);

  const tabConfigs = {
    "Track App": {
      data: TRACK_APP_PAGE_INFO,
      isFlat: true,
    },
    Vision: {
      data: VISION_PAGE_INFO,
      isHierarchical: true,
      getDisableReason: (item, itemKey) => {
        // Don't disable Access fields themselves
        const accessFields = ["Catalog_Catalog Access", "Stocks_Stocks Access", "Collection_Collection Access", "Category_Category Access"];

        if (accessFields.includes(itemKey)) {
          return null; // Never disable Access fields
        }

        if (!itemKey || !itemKey.includes("_")) {
          return null;
        }

        const mainKey = itemKey.split("_")[0];
        let viewKey = "";

        if (mainKey === "Catalog") viewKey = "Catalog_Catalog Access";
        else if (mainKey === "Stocks") viewKey = "Stocks_Stocks Access";
        else if (mainKey === "Collection") viewKey = "Collection_Collection Access";
        else if (mainKey === "Category") viewKey = "Category_Category Access";

        if (viewKey) {
          const hasView = checkedState[viewKey] && permissionSelection[viewKey]?.includes("View");
          if (!hasView) {
            return `Requires ${mainKey} Access View`;
          }
        }

        return null;
      },
    },
    "My Binny's": {
      data: MYBINNYS_APP_PAGE_INFO,
      isFlat: true,
      getDisableReason: (item) => {
        const hasClientManagementView = checkedState["Client Management"] && permissionSelection["Client Management"]?.includes("View");
        const isUserOrReport = item.name === "User Management" || item.name === "Report Management";
        return isUserOrReport && !hasClientManagementView ? "Requires Client Management View" : null;
      },
    },
    Create: {
      data: CREATE_PAGE_INFO,
      isHierarchical: true,
      getDisableReason: (item, itemKey) => {
        const isCadUser = selectedUserType === "CAD User";

        if (isCadUser) {
          const cadEnabledMenus = ["CAD", "CAM", "Sizing", "Modification", "Miscellaneous Settings"];

          const isMainItem = !itemKey?.includes("_");

          if (isMainItem) {
            if (!cadEnabledMenus.includes(item.name)) {
              return "Not available for CAD users";
            }
          } else {
            const parentMenu = itemKey?.split("_")[0];
            if (parentMenu && !cadEnabledMenus.includes(parentMenu)) {
              return "Not available for CAD users";
            }

            const subMenuName = itemKey?.split("_")[1];
            const isAccessItem = subMenuName?.endsWith("Access");
            const isCadComments = parentMenu === "Miscellaneous Settings" && subMenuName === "Comments";

            if (!isAccessItem && !isCadComments) {
              return "Not available for CAD users";
            }

            const mainKey = itemKey.split("_")[0];
            const accessKey = `${mainKey}_${mainKey} Access`;
            const isActionItem = itemKey.includes("Actions") || itemKey.includes("Comments") || itemKey.includes("Item Images");

            if (isActionItem) {
              const hasParentAccess = CREATE_PAGE_INFO.some((menu) => menu.name === mainKey && menu.subMenus?.some((sub) => sub.name === `${mainKey} Access`));
              if (hasParentAccess) {
                const hasAccessView = checkedState[accessKey] && permissionSelection[accessKey]?.includes("View");
                if (!hasAccessView) {
                  return `Requires ${mainKey} Access View`;
                }
              }
            }
          }
          return null;
        }

        // Non-CAD: grey out Actions / Comments / Item Images when parent * Access has no View (matches handleSwitchChange)
        if (itemKey?.includes("_")) {
          const mainKey = itemKey.split("_")[0];
          const accessKey = `${mainKey}_${mainKey} Access`;
          const isActionItem = itemKey.includes("Actions") || itemKey.includes("Comments") || itemKey.includes("Item Images");

          if (isActionItem) {
            const hasParentAccess = CREATE_PAGE_INFO.some((menu) => menu.name === mainKey && menu.subMenus?.some((sub) => sub.name === `${mainKey} Access`));
            if (hasParentAccess) {
              const hasAccessView = checkedState[accessKey] && permissionSelection[accessKey]?.includes("View");
              if (!hasAccessView) {
                return `Requires ${mainKey} Access View`;
              }
            }
          }
        }

        return null;
      },
    },
    Select: {
      data: SELECT_PAGE_INFO,
      isFlat: true,
      getDisableReason: (item) => {
        const hasBinnysView = permissionSelection["Binny's Select Access"]?.includes("View");
        if (item.name === "Quotes" || item.name === "Share Access") {
          return !hasBinnysView ? "Requires Binny's Select Access View" : null;
        }
        return null;
      },
    },
    "Scan App": {
      data: SCAN_APP_PAGE_INFO,
      isScanApp: true,
      isHierarchical: true,
      getDisableReason: (item, itemKey) => {
        const isScanAppAccessEnabled = !!checkedState["Scan App Access"];
        if (itemKey !== "Scan App Access" && !isScanAppAccessEnabled) {
          return "Scan App Access required";
        }
        return null;
      },
    },
    Nexus: {
      data: NEXUS_WEB_PAGE_INFO,
      isScanApp: true,
      isHierarchical: true,
      getDisableReason: (item, itemKey) => {
        const isNexusWebAccessEnabled = !!checkedState["Nexus Web Access"];
        if (itemKey !== "Nexus Web Access" && !isNexusWebAccessEnabled) {
          return "Nexus Web Access required";
        }
        return null;
      },
    },
    Fab: {
      data: FAB_PAGE_INFO,
      isScanApp: true,
      isFlat: true,
      getDisableReason: (item, itemKey) => {
        const isFabAccessEnabled = !!checkedState["Fab Web Access"];
        if (itemKey !== "Fab Web Access" && !isFabAccessEnabled) {
          return "Fab Web Access required";
        }
        return null;
      },
    },
    Breco: {
      data: BRECO_PAGE_INFO,
      isScanApp: true,
      isHierarchical: true,
      getDisableReason: (item, itemKey) => {
        const isBrecoAccessEnabled = !!checkedState["Breco Web Access"];
        if (itemKey !== "Breco Web Access" && !isBrecoAccessEnabled) {
          return "Breco Web Access required";
        }
        return null;
      },
    },
    Aira: {
      data: AIRA_APP_PAGE_INFO,
      isScanApp: true,
      isFlat: true,
      getDisableReason: (item, itemKey) => {
        const isAiraAccessEnabled = !!checkedState["Aira App Access"];
        if (itemKey !== "Aira App Access" && !isAiraAccessEnabled) {
          return "Aira App Access required";
        }
        return null;
      },
    },
    Expo: {
      data: EXPO_PAGE_INFO,
      isScanApp: true,
      isFlat: true,
      getDisableReason: (item, itemKey) => {
        const isExpoAccessEnabled = !!checkedState["Expense tracker Web Access"];
        if (itemKey !== "Expense tracker Web Access" && !isExpoAccessEnabled) {
          return "Expense tracker Web Access required";
        }
        return null;
      },
    },
    Creco: {
      data: CRECO_PAGE_INFO,
      isScanApp: true,
      isFlat: true,
      getDisableReason: (item, itemKey) => {
        const isCrecoAccessEnabled = !!checkedState["Creco Web Access"];
        if (itemKey !== "Creco Web Access" && !isCrecoAccessEnabled) {
          return "Creco Web Access required";
        }
        return null;
      },
    },
    Techie: {
      data: TECHIE_PAGE_INFO,
      isScanApp: true,
      isFlat: true
    },
    "Client Master": {
      data: ClientMaster_PAGE_INFO,
      isScanApp: true,
      isFlat: true,
      getDisableReason: (item, itemKey) => {
        const isClientMasterAppAccessEnabled = !!checkedState["Client Master App Access"];
        if (itemKey !== "Client Master App Access" && !isClientMasterAppAccessEnabled) {
          return "Client Master App Access required";
        }
        return null;
      },
    },
    Purchie: {
      data: PURCHIE_PAGE_INFO,
      isFlat: true,
    },
    CRM: {
      data: CRM_PAGE_INFO,
      isHierarchical: true,
      flatSubKeys: true,
      getDisableReason: (item, itemKey) => {
        if (isCrmParentOn(itemKey, checkedState)) return null;
        return `Needs ${CRM_PARENTS[itemKey].join(" or ")}`;
      },
    },
  };

  const popoverOpen = Boolean(anchorEl);

  return (
    <div
      className="new-right-container"
      style={{
        padding: "16px 32px",
        height: "80vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        className="tabs-container"
        style={{
          marginBottom: "15px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          position: "sticky",
          top: 0,
          backgroundColor: "#000000ff",
          zIndex: 10,
          paddingBottom: "10px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "30px", overflowX: "auto", scrollbarWidth: "none", msOverflowStyle: "none", paddingBottom: "4px" }}>
          {TABS.map((tab) => {
            let displayName = tabNames[tab];

            if(!permission?.isAdmin && (tab === "Nexus" || tab === "Fab" || tab === "Breco" || tab === "Aira" || tab === "Techie" || tab === "Creco" || tab === "Expo" || tab === "Track App" || tab === "Client Master" || tab === "Purchie" || tab === "CRM")) return;

            return (
              <Typography
                key={tab}
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 600,
                  fontSize: "16px",
                  flexShrink: 0,
                  cursor: "pointer",
                  color: activeTab === tab ? "#fff" : "#666",
                  position: "relative",
                  paddingBottom: "8px",
                  "&:hover": {
                    color: "#fff",
                  },
                }}
                onClick={() => {
                  if (tab !== activeTab) handleTabSwitch(tab);
                }}
              >
                {displayName}
                {activeTab === tab && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "3px",
                      backgroundColor: "#ffffffff",
                      borderRadius: "2px",
                      margin: 0,
                      border: "none",
                    }}
                  />
                )}
              </Typography>
            );
          })}
        </div>
        {collapsedButtons.includes(activeTab) && (
          <button
            onClick={handleToggleAll}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "#000000ff",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 500,
              fontSize: "14px",
              transition: "all 0.2s",
              justifyContent: "center",
            }}
          >
            {allCollapsed ? <KeyboardDoubleArrowRight sx={{ fontSize: "25px" }} /> : <KeyboardDoubleArrowDownIcon sx={{ fontSize: "25px" }} />}
          </button>
        )}
      </div>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          height: "calc(100vh - 180px)",
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          position: "relative",
          overscrollBehavior: "contain",
        }}
      >
        {activeTab === "Create" && renderUserTypeSelector()}
        <RightsTable
          key={activeTab}
          data={tabConfigs[activeTab]?.data}
          checkedState={checkedState}
          permissionSelection={permissionSelection}
          expanded={expanded}
          onToggleExpand={handleToggleExpand}
          onSwitchChange={handleSwitchChange}
          onOptionToggle={handleOptionToggle}
          onPopoverOpen={handlePopoverOpen}
          onPopoverClose={handlePopoverClose}
          currentPopoverItem={currentPopoverItem}
          anchorEl={anchorEl}
          isDisabled={!EditAccessNewRights || isAdmin}
          isAdmin={isAdmin}
          isScanApp={tabConfigs[activeTab]?.isScanApp || false}
          isNexusWeb={tabConfigs[activeTab]?.isNexusWeb || false}
          isClientMasterApp={tabConfigs[activeTab]?.isClientMasterApp || false}
          getDisableReason={tabConfigs[activeTab]?.getDisableReason}
          getRestrictedOptionsForItem={getRestrictedOptionsForItem}
          selectedUserType={selectedUserType} // Add this line
          flatSubKeys={tabConfigs[activeTab]?.flatSubKeys || false}
          renderSectionExtras={tabConfigs[activeTab]?.renderSectionExtras || null}
        />
        {activeTab === "Breco" && (
          <Box style={{ marginBottom: "8px" }}>
            <div
              className="row"
              onClick={() => {
                if (checkedState["Breco Web Access"]) setBrecoGroupsExpanded((prev) => !prev);
              }}
              style={{ cursor: checkedState["Breco Web Access"] ? "pointer" : "default" }}
            >
              <div className="row-content">
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {brecoGroupsExpanded ? (
                    <KeyboardArrowDownIcon sx={{ color: checkedState["Breco Web Access"] ? "#fff" : "#888", fontSize: "20px" }} />
                  ) : (
                    <KeyboardArrowRightIcon sx={{ color: checkedState["Breco Web Access"] ? "#fff" : "#888", fontSize: "20px" }} />
                  )}
                  <Typography className="row-label" style={{ width: "100%", flex: 1, color: checkedState["Breco Web Access"] ? "#fff" : "#888" }}>
                    Client Groups
                  </Typography>
                </div>
              </div>
            </div>
            <Collapse in={brecoGroupsExpanded && !!checkedState["Breco Web Access"]}>
              <div style={{ backgroundColor: "#000000ff", overflow: "hidden", height: "auto" }}>
                {brecoGroups.length === 0 ? (
                  <div className="row" style={{ marginLeft: 20 }}>
                    <div className="row-content">
                      <Typography className="row-label" style={{ color: "#888" }}>
                        No client groups found
                      </Typography>
                    </div>
                  </div>
                ) : (
                  brecoGroups.map((group) => {
                    const groupDisabled = !EditAccessNewRights || isAdmin || !checkedState["Breco Web Access"];
                    return (
                      <div key={group.id} className="row" style={{ marginLeft: 20 }}>
                        <div className="row-content">
                          <Typography className="row-label" style={{ color: groupDisabled ? "#888" : "#fff" }}>
                            {group.name}
                          </Typography>
                          <div className="right-content row-actions">
                            <Switch
                              className="ios-switch"
                              checked={brecoGroupIds.includes(group.id)}
                              disableRipple
                              disabled={groupDisabled}
                              onClick={(e) => {
                                if (groupDisabled) return;
                                e.stopPropagation();
                                handleBrecoGroupToggle(group.id);
                              }}
                              sx={{
                                opacity: groupDisabled || isAdmin ? 0.6 : 1,
                                "& .MuiSwitch-thumb": {
                                  backgroundColor: isAdmin ? "#888" : "#fff",
                                },
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Collapse>
          </Box>
        )}
        <Popover
          open={popoverOpen}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              backgroundColor: "#fff",
              color: "#000",
              padding: "8px 12px",
              borderRadius: "6px",
              marginTop: "6px",
              boxShadow: "0px 6px 18px rgba(0,0,0,0.2)",
              maxHeight: "250px",
              overflowY: "auto",
              width: "fit-content",
              minWidth: "auto",
            },
          }}
        >
          {currentPopoverItem && (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {currentPopoverItem?.options
                ?.filter((option) => {
                  const itemKey = currentPopoverItem?.isSubMenu ? currentPopoverItem?.subItemName : currentPopoverItem?.name;
                  return !getRestrictedOptionsForItem(itemKey).includes(option);
                })
                ?.map((option) => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        icon={<CheckBoxOutlineBlankRoundedIcon sx={{ fontSize: 23, color: "#474747ff" }} />}
                        checkedIcon={<CheckBoxRoundedIcon sx={{ fontSize: 23, color: "#50C878" }} />}
                        checked={
                          currentPopoverItem?.isSubMenu ? permissionSelection[currentPopoverItem?.subItemName]?.includes(option) : permissionSelection[currentPopoverItem?.name]?.includes(option)
                        }
                        onChange={() => {
                          if (currentPopoverItem?.isSubMenu) {
                            handleOptionToggle(currentPopoverItem?.subItemName, option);
                          } else {
                            handleOptionToggle(currentPopoverItem?.name, option);
                          }
                        }}
                        sx={{ m: 0, p: 0, ml: 1 }}
                      />
                    }
                    label={<Typography sx={{ fontFamily: "Karla, sans-serif", fontWeight: 400, fontSize: "14px", lineHeight: "32px", color: "#000", marginLeft: "6px" }}>{option}</Typography>}
                  />
                ))}
            </Box>
          )}
        </Popover>
      </Box>

      <RemoveModal open={saveRightModalOpen} setOpen={setSaveRightModalOpen} btnInfo={modalBtnInfo} heading="Confirm granting this role these rights?" oneLiner={true} />
    </div>
  );
};

export default NewRightsPage;
